// Product Repository - Repository Pattern
import apiService from '../api.service';

class ProductRepository {
  // Get all products with relations
  async getAll(params = {}) {
    return apiService.get('/products', {
      ...params,
      with: 'category,warehouse', // Include relations
    });
  }

  // Get single product with relations
  async getById(id) {
    return apiService.get(`/products/${id}`, {
      with: 'category,warehouse',
    });
  }

  // Create product
  async create(data) {
    // Handle FormData for file uploads
    const formData = new FormData();
    
    console.log('Creating product with data:', {
      ...data,
      mainImage: data.mainImage ? 'File' : null,
      imagesCount: Array.isArray(data.images) ? data.images.length : 0
    });
    
    // Add all fields to FormData
    Object.keys(data).forEach(key => {
      if (key === 'mainImage' && data[key]) {
        formData.append('main_image', data[key]);
      } else if (key === 'images' && Array.isArray(data[key]) && data[key].length > 0) {
        // Laravel expects images as array - use images[] format
        data[key].forEach((image) => {
          if (image instanceof File) {
            formData.append('images[]', image);
          }
        });
      } else if (key === 'tags' && Array.isArray(data[key])) {
        data[key].forEach((tag, index) => {
          formData.append(`tags[${index}]`, tag);
        });
      } else if (key !== 'mainImage' && key !== 'images' && key !== 'imageUrls' && key !== 'mainImageUrl') {
        // Skip display-only fields and already handled fields
        if (data[key] !== null && data[key] !== undefined && data[key] !== '') {
          formData.append(key, data[key]);
        }
      }
    });
    
    // Debug: Log FormData contents
    console.log('FormData entries:');
    for (let pair of formData.entries()) {
      console.log(pair[0] + ': ' + (pair[1] instanceof File ? `File: ${pair[1].name}` : pair[1]));
    }
    
    // Don't set Content-Type header for FormData - browser will set it automatically with boundary
    return apiService.post('/products', formData);
  }

  // Update product
  async update(id, data) {
    // Handle FormData for file uploads (same as create)
    const formData = new FormData();
    
    // Add all fields to FormData - include all fields even if empty for updates
    Object.keys(data).forEach(key => {
      if (key === 'mainImage' && data[key]) {
        formData.append('main_image', data[key]);
      } else if (key === 'images' && Array.isArray(data[key]) && data[key].length > 0) {
        data[key].forEach((image, index) => {
          if (image instanceof File) {
            formData.append(`images[${index}]`, image);
          }
        });
      } else if (key === 'existingGalleryImageIds' && Array.isArray(data[key])) {
        // FormData cannot represent an empty array. Always send a JSON payload so
        // clearing the whole gallery (ids = []) is explicit and reliable.
        formData.append('existing_gallery_image_ids_json', JSON.stringify(data[key]));
        formData.append('sync_gallery_images', '1');
        data[key].forEach((imageId, index) => {
          formData.append(`existing_gallery_image_ids[${index}]`, String(imageId));
        });
      } else if (key === 'removeMainImage') {
        formData.append('remove_main_image', data[key] ? '1' : '0');
      } else if (key === 'tags' && Array.isArray(data[key])) {
        // Send tags as array
        data[key].forEach((tag, index) => {
          formData.append(`tags[${index}]`, tag);
        });
      } else if (key === 'stockAlert') {
        // Send stockAlert as stock_alert (backend expects stock_alert)
        // Always send stock_alert, even if empty (for updates)
        const value = data[key] === null || data[key] === undefined ? '' : String(data[key]);
        formData.append('stock_alert', value);
      } else if (key !== 'mainImage' && key !== 'images' && key !== 'imageUrls' && key !== 'mainImageUrl' && key !== 'stockAlert' && key !== 'existingGalleryImageIds' && key !== 'removeMainImage') {
        // Include all other fields - ALWAYS send them for updates (even if empty)
        // This ensures all fields are sent to backend for proper update
        const value = data[key] === null || data[key] === undefined ? '' : String(data[key]);
        formData.append(key, value);
      }
    });
    
    console.log('FormData contents:', Array.from(formData.entries())); // Debug log
    
    // Use POST method for updates with FormData (better support than PATCH)
    // Use the special update route that accepts POST
    const url = `${apiService.baseURL}/products/${id}/update`;
    const token = localStorage.getItem('adminToken');
    
    const config = {
      method: 'POST', // Use POST instead of PATCH for better FormData support
      headers: {
        ...(token && { Authorization: `Bearer ${token}` }),
        // Don't set Content-Type - browser will set it automatically with boundary
      },
      body: formData,
    };
    
    try {
      const response = await fetch(url, config);
      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: 'An error occurred' }));
        throw new Error(error.message || `HTTP error! status: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error('API Request Error:', error);
      throw error;
    }
  }

  // Delete product
  async delete(id) {
    return apiService.delete(`/products/${id}`);
  }

  // Bulk operations
  async bulkDelete(ids) {
    return apiService.post('/products/bulk-delete', { ids });
  }

  async bulkUpdate(ids, data) {
    return apiService.post('/products/bulk-update', { ids, data });
  }

  // Search products
  async search(query) {
    return apiService.get('/products/search', { q: query });
  }
}

export default new ProductRepository();
